import type { BreakpointKey } from "@/state/@types";

import type {
  BreakpointLayouts,
  DashboardGridCard,
  ReportLayoutWidget,
} from "./ReportsView.utils";
import {
  BREAKPOINT_COLUMNS,
  clampCardColSpan,
  getWidgetColSpanConstraints,
} from "./ReportsView.utils";

/** The scroll container's - not the grid itself: its edge inset, and what the grid measures itself against. */
export const REPORTS_GRID_SCROLL_CONTAINER_CLASS =
  "box-border pl-4 pr-4 pt-4 pb-12 @container [container-name:reports-dashboard]";

/** Grid container width at or above this uses the desktop 4-column grid. */
export const REPORTS_MOBILE_MAX_WIDTH = 720; // Theme.breakpoints.values.md

/**
 * How tall one dashboard card is, in both modes.
 *
 * A widget's body is a chart three hundred pixels tall under a header, so a
 * card shorter than this shows a chart with its feet cut off. One number for
 * the stacked page and the grid means a card is the same card at any width,
 * and - because a grid row is this tall too - it means the row a card sits in
 * never has to ask the card how tall it is.
 */
export const REPORTS_CARD_HEIGHT_PX = 400;

/** Fixed column count for desktop grid mode (not mobile stack). */
export const REPORTS_DESKTOP_COLUMNS = 4;

/** Canonical layout keys — only two persisted layout variants. */
export const REPORTS_DESKTOP_LAYOUT_KEY: BreakpointKey = "lg";
export const REPORTS_STACK_LAYOUT_KEY: BreakpointKey = "xs";

const DESKTOP_LAYOUT_KEYS: BreakpointKey[] = ["md", "lg", "xl"];

export type ReportsGridMode = "stack" | "grid";

export function reportsGridModeFromWidth(width: number): ReportsGridMode {
  return width >= REPORTS_MOBILE_MAX_WIDTH ? "grid" : "stack";
}

export function reportsLayoutKeyForMode(mode: ReportsGridMode): BreakpointKey {
  return mode === "grid"
    ? REPORTS_DESKTOP_LAYOUT_KEY
    : REPORTS_STACK_LAYOUT_KEY;
}

export function usesGridPlacementForMode(mode: ReportsGridMode): boolean {
  return mode === "grid";
}

export function isDesktopLayoutBreakpoint(breakpoint: BreakpointKey): boolean {
  return DESKTOP_LAYOUT_KEYS.includes(breakpoint);
}

/** Merge desktop layouts; canonical ``lg`` wins conflicts (legacy md/xl are mirrors). */
export function resolveDesktopLayoutCards(
  layouts: BreakpointLayouts,
): DashboardGridCard[] {
  const byId = new Map<string, DashboardGridCard>();
  const mergeOrder: BreakpointKey[] = ["md", "xl", REPORTS_DESKTOP_LAYOUT_KEY];

  for (const key of mergeOrder) {
    for (const card of layouts[key]?.cards ?? []) {
      byId.set(card.cardId, card);
    }
  }

  return [...byId.values()];
}

/** Mirror desktop layout across md/lg/xl so panel resize does not swap layouts. */
export function canonicalizeReportLayouts(
  layouts: BreakpointLayouts,
  widgetsById: Map<string, ReportLayoutWidget>,
): BreakpointLayouts {
  const desktopCards = resolveDesktopLayoutCards(layouts);
  if (!desktopCards.length) {
    return layouts;
  }

  const placed = ensureGridPlacement(
    desktopCards,
    widgetsById,
    REPORTS_DESKTOP_COLUMNS,
  );

  const next = { ...layouts };
  for (const key of DESKTOP_LAYOUT_KEYS) {
    next[key] = { cards: placed };
  }
  return next;
}

/** Mirror stack layout across xs/sm. */
export function canonicalizeStackReportLayouts(
  layouts: BreakpointLayouts,
): BreakpointLayouts {
  const stackCards =
    layouts[REPORTS_STACK_LAYOUT_KEY]?.cards ?? layouts.sm?.cards;
  if (!stackCards?.length) {
    return layouts;
  }

  return {
    ...layouts,
    xs: { cards: stackCards },
    sm: { cards: stackCards },
  };
}

/** A card that has been given its grid lines, so it sits on known squares. */
export type PlacedGridCard = DashboardGridCard & {
  colStart: number;
  colEnd: number;
  rowStart: number;
  rowEnd: number;
};

export function hasGridPlacement(
  card: DashboardGridCard,
): card is PlacedGridCard {
  return (
    card.colStart != null &&
    card.colEnd != null &&
    card.rowStart != null &&
    card.rowEnd != null
  );
}

export function rowSpanForCard(card: DashboardGridCard): number {
  if (hasGridPlacement(card)) {
    return Math.max(1, card.rowEnd - card.rowStart);
  }
  return 1;
}

/** Pixel height for a dashboard card from its grid row span and track size. */
export function gridCardBodyHeight(
  card: DashboardGridCard,
  mode: ReportsGridMode,
  rowHeightPx: number,
  gapPx: number,
): string {
  if (!usesGridPlacementForMode(mode) || !hasGridPlacement(card)) {
    return `${rowHeightPx}px`;
  }
  const span = rowSpanForCard(card);
  const total = span * rowHeightPx + Math.max(0, span - 1) * gapPx;
  return `${total}px`;
}

export function clampCardGridPlacement(
  card: DashboardGridCard,
  widget: ReportLayoutWidget | undefined,
  columns: number,
): DashboardGridCard {
  if (!hasGridPlacement(card)) {
    return card;
  }

  const minWidth = clampCardColSpan(1, widget, columns);
  const maxWidth = columns;

  let colStart = Math.max(1, card.colStart);
  let colEnd = Math.min(
    columns + 1,
    Math.max(colStart + minWidth, card.colEnd),
  );
  if (colEnd - colStart > maxWidth) {
    colEnd = colStart + maxWidth;
  }
  if (colEnd - colStart < minWidth) {
    colStart = Math.max(1, colEnd - minWidth);
  }

  const rowStart = Math.max(1, card.rowStart);
  const rowEnd = Math.max(rowStart + 1, card.rowEnd);

  return {
    ...card,
    colStart,
    colEnd,
    rowStart,
    rowEnd,
    colSpan: colEnd - colStart,
  };
}

export function gridCellFromPointer(
  gridRect: { left: number; top: number; width: number },
  pointerX: number,
  pointerY: number,
  columns: number,
  rowHeightPx: number,
  gapPx: number,
): { col: number; row: number } {
  const x = Math.max(0, pointerX - gridRect.left);
  const y = Math.max(0, pointerY - gridRect.top);

  const totalGapX = Math.max(0, columns - 1) * gapPx;
  const colWidth = (gridRect.width - totalGapX) / columns;

  let col = 1;
  let cursor = 0;
  for (let index = 0; index < columns; index += 1) {
    const end = cursor + colWidth;
    if (x < end || index === columns - 1) {
      col = index + 1;
      break;
    }
    cursor = end + gapPx;
  }

  const rowStep = rowHeightPx + gapPx;
  const row = Math.max(1, Math.floor(y / rowStep) + 1);

  return { col, row };
}

export function sameDashboardCardOrder(
  left: DashboardGridCard[],
  right: DashboardGridCard[],
): boolean {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((card, index) => card.cardId === right[index]?.cardId);
}

/**
 * Where a dragged card lands, given the page as it stood when the drag began.
 *
 * Wherever it is let go - empty space, or on top of another widget - the card
 * goes there. A widget it lands on moves into the space the card left, if it
 * fits there - two cards the same size change places - and slides down out of
 * the way if it does not. Nothing else moves.
 *
 * It used to be different over another widget: the card took that widget's
 * place in reading order and the whole page was flowed again around it. So a
 * card carried across the page reshuffled everything it passed - flowed into
 * an empty column on the far side, a chart filled it, and the column looked
 * taken until the pointer reached it. Before that, over empty space too, the
 * page was flowed, and a card fell into the first gap it fit rather than the
 * space it was dropped in.
 *
 * ``cards`` are placed (``ensureGridPlacement``), in reading order. ``grab`` is
 * the square of the card the pointer holds it by, counted from its top-left,
 * so the card keeps its place under the pointer instead of jumping to put its
 * corner there.
 */
export function dragCardToGridCell(
  cards: DashboardGridCard[],
  activeId: string,
  col: number,
  row: number,
  grab: { col: number; row: number },
  columns: number,
): DashboardGridCard[] | null {
  const activeCard = cards.find((card) => card.cardId === activeId);
  if (!activeCard || !hasGridPlacement(activeCard)) {
    return null;
  }

  const width = activeCard.colEnd - activeCard.colStart;
  const rowSpan = rowSpanForCard(activeCard);
  const colStart = Math.max(1, Math.min(columns - width + 1, col - grab.col));
  const rowStart = Math.max(1, row - grab.row);
  const landed: PlacedGridCard = {
    ...activeCard,
    colStart,
    colEnd: colStart + width,
    rowStart,
    rowEnd: rowStart + rowSpan,
  };

  // The widgets it landed on, in reading order, move into the space it left:
  // each to the first spot there it fits whole, clear of the card and of
  // everything else.
  const placed = new Map<string, DashboardGridCard>(
    cards.map((card) => [card.cardId, card]),
  );
  placed.set(activeId, landed);
  const left = activeCard;
  for (const card of sortCardsByGridPosition(cards)) {
    if (
      card.cardId === activeId ||
      !card.visible ||
      !hasGridPlacement(card) ||
      !cardsOverlap(card, landed)
    ) {
      continue;
    }
    const cardWidth = card.colEnd - card.colStart;
    const cardRows = card.rowEnd - card.rowStart;
    search: for (
      let top = left.rowStart;
      top + cardRows <= left.rowEnd;
      top += 1
    ) {
      for (
        let side = left.colStart;
        side + cardWidth <= left.colEnd;
        side += 1
      ) {
        const candidate = {
          ...card,
          colStart: side,
          colEnd: side + cardWidth,
          rowStart: top,
          rowEnd: top + cardRows,
        };
        const clear = [...placed.values()].every(
          (other) =>
            other.cardId === card.cardId ||
            !other.visible ||
            !cardsOverlap(candidate, other),
        );
        if (clear) {
          placed.set(card.cardId, candidate);
          break search;
        }
      }
    }
  }

  // Whatever did not fit there slides down out of the way.
  return separateOverlappingCards(
    cards.map((card) => placed.get(card.cardId) ?? card),
    columns,
    activeId,
  );
}

/** Row-major order so flow-packing matches the current grid layout on drag start. */
export function sortCardsByGridPosition(
  cards: DashboardGridCard[],
): DashboardGridCard[] {
  return [...cards].sort((a, b) => {
    const rowA = a.rowStart ?? Number.MAX_SAFE_INTEGER;
    const rowB = b.rowStart ?? Number.MAX_SAFE_INTEGER;
    if (rowA !== rowB) return rowA - rowB;
    const colA = a.colStart ?? Number.MAX_SAFE_INTEGER;
    const colB = b.colStart ?? Number.MAX_SAFE_INTEGER;
    return colA - colB;
  });
}

type PackCardsToGridOptions = {
  preserveRowSpan?: boolean;
  /** Cards that keep their current grid lines; others flow around them. */
  pinnedCardIds?: ReadonlySet<string>;
};

function ensureOccupancyRow(
  occupied: boolean[][],
  rowIndex: number,
  columns: number,
) {
  while (occupied.length <= rowIndex) {
    occupied.push(Array.from({ length: columns }, () => false));
  }
  const row = occupied[rowIndex];
  while (row.length < columns) {
    row.push(false);
  }
}

function isGridRegionFree(
  occupied: boolean[][],
  colStart: number,
  colEnd: number,
  rowStart: number,
  rowEnd: number,
  columns: number,
): boolean {
  if (
    colStart < 1 ||
    colEnd > columns + 1 ||
    rowStart < 1 ||
    rowEnd <= rowStart
  ) {
    return false;
  }

  for (let row = rowStart - 1; row < rowEnd - 1; row += 1) {
    ensureOccupancyRow(occupied, row, columns);
    for (let col = colStart - 1; col < colEnd - 1; col += 1) {
      if (occupied[row][col]) {
        return false;
      }
    }
  }

  return true;
}

function markGridRegionOccupied(
  occupied: boolean[][],
  colStart: number,
  colEnd: number,
  rowStart: number,
  rowEnd: number,
  columns: number,
) {
  for (let row = rowStart - 1; row < rowEnd - 1; row += 1) {
    ensureOccupancyRow(occupied, row, columns);
    for (let col = colStart - 1; col < colEnd - 1; col += 1) {
      occupied[row][col] = true;
    }
  }
}

function markCardOccupied(
  occupied: boolean[][],
  card: DashboardGridCard,
  columns: number,
) {
  if (!hasGridPlacement(card)) return;
  markGridRegionOccupied(
    occupied,
    card.colStart,
    card.colEnd,
    card.rowStart,
    card.rowEnd,
    columns,
  );
}

function findFirstGridFit(
  occupied: boolean[][],
  width: number,
  rowSpan: number,
  columns: number,
): { colStart: number; rowStart: number } {
  let rowStart = 1;

  while (true) {
    for (let colStart = 1; colStart <= columns - width + 1; colStart += 1) {
      if (
        isGridRegionFree(
          occupied,
          colStart,
          colStart + width,
          rowStart,
          rowStart + rowSpan,
          columns,
        )
      ) {
        return { colStart, rowStart };
      }
    }
    rowStart += 1;
  }
}

/** Flow-pack cards in list order into explicit grid coordinates. */
export function packCardsToGrid(
  cards: DashboardGridCard[],
  widgetsById: Map<string, ReportLayoutWidget>,
  columns: number,
  options?: PackCardsToGridOptions,
): DashboardGridCard[] {
  const preserveRowSpan = options?.preserveRowSpan ?? false;
  const pinnedCardIds = options?.pinnedCardIds;
  const occupied: boolean[][] = [];

  if (pinnedCardIds?.size) {
    for (const card of cards) {
      if (!pinnedCardIds.has(card.cardId) || !hasGridPlacement(card)) {
        continue;
      }
      markCardOccupied(
        occupied,
        clampCardGridPlacement(card, widgetsById.get(card.cardId), columns),
        columns,
      );
    }
  }

  return cards.map((card) => {
    const widget = widgetsById.get(card.cardId);
    const defaultSpan = widget
      ? getWidgetColSpanConstraints(widget, columns).defaultColSpan
      : 1;
    const width = clampCardColSpan(
      card.colSpan > 0 ? card.colSpan : defaultSpan,
      widget,
      columns,
    );
    const rowSpan = preserveRowSpan ? rowSpanForCard(card) : 1;

    if (pinnedCardIds?.has(card.cardId) && hasGridPlacement(card)) {
      const placed = clampCardGridPlacement(
        { ...card, colSpan: width },
        widget,
        columns,
      );
      markCardOccupied(occupied, placed, columns);
      return placed;
    }

    const { colStart, rowStart } = findFirstGridFit(
      occupied,
      width,
      rowSpan,
      columns,
    );
    const placed: DashboardGridCard = {
      ...card,
      colSpan: width,
      colStart,
      colEnd: colStart + width,
      rowStart,
      rowEnd: rowStart + rowSpan,
    };

    markCardOccupied(occupied, placed, columns);
    return placed;
  });
}

/** Copy explicit grid lines from ``source`` when ``target`` only has span metadata. */
export function mergeCardGridPlacement(
  target: DashboardGridCard,
  source: DashboardGridCard | undefined,
): DashboardGridCard {
  if (!source || hasGridPlacement(target) || !hasGridPlacement(source)) {
    return target;
  }

  return {
    ...target,
    colSpan: source.colSpan,
    colStart: source.colStart,
    colEnd: source.colEnd,
    rowStart: source.rowStart,
    rowEnd: source.rowEnd,
  };
}

/** Whether two cards stand on any of the same squares. */
function cardsOverlap(
  left: DashboardGridCard,
  right: DashboardGridCard,
): boolean {
  return (
    hasGridPlacement(left) &&
    hasGridPlacement(right) &&
    left.colStart < right.colEnd &&
    left.colEnd > right.colStart &&
    left.rowStart < right.rowEnd &&
    left.rowEnd > right.rowStart
  );
}

/**
 * The first two cards found sitting on the same square, if there are any.
 *
 * A card that is not shown is not on the page, so it is neither overlapped
 * nor in anybody's way - it keeps whatever square it had for when it comes
 * back.
 */
export function findOverlappingCards(
  cards: DashboardGridCard[],
): [DashboardGridCard, DashboardGridCard] | null {
  const shown = cards.filter((card) => card.visible);
  for (let index = 0; index < shown.length; index += 1) {
    for (let other = index + 1; other < shown.length; other += 1) {
      if (cardsOverlap(shown[index], shown[other])) {
        return [shown[index], shown[other]];
      }
    }
  }
  return null;
}

/**
 * Give every widget on the page a square of its own.
 *
 * Taken in reading order, a card that wants a square another one already has
 * slides straight down until the space it wants is free - it keeps its
 * columns and its height, so the page is recognisably the one that was saved.
 * Nothing that already had room to itself is moved.
 *
 * This is where a layout stops being able to overlap: every read and every
 * write of a report definition is normalized through ``ensureGridPlacement``,
 * so a layout saved by an older build - or one a future bug computes - is
 * pulled apart before anything is drawn from it.
 */
function separateOverlappingCards(
  cards: DashboardGridCard[],
  columns: number,
  /** A card that keeps its square: the others make way for it. */
  keptCardId?: string,
): DashboardGridCard[] {
  const occupied: boolean[][] = [];
  const moved = new Map<string, DashboardGridCard>();
  const ordered = sortCardsByGridPosition(cards);
  const kept = ordered.findIndex((card) => card.cardId === keptCardId);
  if (kept > 0) {
    ordered.unshift(...ordered.splice(kept, 1));
  }

  for (const card of ordered) {
    if (!card.visible || !hasGridPlacement(card)) continue;

    const colStart = Math.max(1, Math.min(columns, card.colStart));
    const colEnd = Math.max(colStart + 1, Math.min(columns + 1, card.colEnd));
    const rowSpan = Math.max(1, card.rowEnd - card.rowStart);
    let rowStart = Math.max(1, card.rowStart);
    while (
      !isGridRegionFree(
        occupied,
        colStart,
        colEnd,
        rowStart,
        rowStart + rowSpan,
        columns,
      )
    ) {
      rowStart += 1;
    }

    const placed = {
      ...card,
      colStart,
      colEnd,
      colSpan: colEnd - colStart,
      rowStart,
      rowEnd: rowStart + rowSpan,
    };
    markCardOccupied(occupied, placed, columns);
    moved.set(card.cardId, placed);
  }

  // Returned in the order they came in: the list order is what a drag
  // reorders, and only the rows were in question here.
  return cards.map((card) => moved.get(card.cardId) ?? card);
}

export function ensureGridPlacement(
  cards: DashboardGridCard[],
  widgetsById: Map<string, ReportLayoutWidget>,
  columns: number,
): DashboardGridCard[] {
  const clampedSpans = cards.map((card) => {
    const widget = widgetsById.get(card.cardId);
    return {
      ...card,
      colSpan: clampCardColSpan(
        card.colSpan > 0 ? card.colSpan : 1,
        widget,
        columns,
      ),
    };
  });

  const placed = clampedSpans.filter(hasGridPlacement);
  const unplaced = clampedSpans.filter((card) => !hasGridPlacement(card));

  if (placed.length === 0) {
    return packCardsToGrid(clampedSpans, widgetsById, columns);
  }

  const clampedPlaced = placed.map((card) =>
    clampCardGridPlacement(card, widgetsById.get(card.cardId), columns),
  );

  if (unplaced.length === 0) {
    return separateOverlappingCards(clampedPlaced, columns);
  }

  const maxRowEnd = clampedPlaced.reduce(
    (max, card) => Math.max(max, card.rowEnd ?? 1),
    1,
  );
  const rowOffset = maxRowEnd - 1;
  const packedUnplaced = packCardsToGrid(unplaced, widgetsById, columns).map(
    (card) => {
      if (!hasGridPlacement(card)) return card;
      return {
        ...card,
        rowStart: card.rowStart + rowOffset,
        rowEnd: card.rowEnd + rowOffset,
      };
    },
  );

  return separateOverlappingCards(
    [...clampedPlaced, ...packedUnplaced],
    columns,
  );
}

export type GridPlacementStyle = {
  gridColumn: string;
  gridRow: string;
};

export function getCardGridStyle(
  card: DashboardGridCard,
  mode: ReportsGridMode,
): GridPlacementStyle {
  if (!usesGridPlacementForMode(mode)) {
    return { gridColumn: "1 / -1", gridRow: "auto" };
  }

  if (hasGridPlacement(card)) {
    return {
      gridColumn: `${card.colStart} / ${card.colEnd}`,
      gridRow: `${card.rowStart} / ${card.rowEnd}`,
    };
  }

  return { gridColumn: `span ${card.colSpan}`, gridRow: "auto" };
}

/**
 * Drag one side of a card, and let the page give way around it - only as far
 * as it has to.
 *
 * Growing into a neighbour it is against takes the room off that neighbour,
 * as long as the neighbour has room to give; one already at its smallest
 * stops the drag, except downwards, where there is always another row and
 * what is underneath is pushed down instead. Growing into empty space takes
 * the space. Shrinking only ever shrinks the card being dragged: the space it
 * gives up is left empty, not handed to whatever is beside it.
 *
 * Nothing moves that does not have to: a widget nobody is against is not
 * moved by a drag two rows away. A drag of several squares goes as far as it
 * can and stops there, because a pointer that outruns the page should leave
 * the widget against the wall rather than snapped back to where it started.
 *
 * Returns the whole layout, or null when there was no room to move at all -
 * which is also how the page knows which ways an edge can be dragged.
 */
export function resizeCardsInGrid(
  cards: DashboardGridCard[],
  cardId: string,
  side: "left" | "right" | "top" | "bottom",
  delta: number,
  widgetsById: Map<string, ReportLayoutWidget>,
  columns: number,
): DashboardGridCard[] | null {
  if (!delta) return null;

  // A square at a time, so each one meets the page the last one left.
  if (Math.abs(delta) > 1) {
    let resized: DashboardGridCard[] | null = null;
    for (let step = 0; step < Math.abs(delta); step += 1) {
      const next = resizeCardsInGrid(
        resized ?? cards,
        cardId,
        side,
        Math.sign(delta),
        widgetsById,
        columns,
      );
      if (!next) break;
      resized = next;
    }
    return resized;
  }

  const placed = ensureGridPlacement(cards, widgetsById, columns);
  const target = placed.find((card) => card.cardId === cardId);
  if (!target || !hasGridPlacement(target)) return null;

  const widthFloor = (card: DashboardGridCard) =>
    clampCardColSpan(1, widgetsById.get(card.cardId), columns);
  /** Whether two cards share a row, which is what makes them neighbours. */
  const sharesRows = (card: DashboardGridCard) =>
    hasGridPlacement(card) &&
    card.rowStart < target.rowEnd &&
    card.rowEnd > target.rowStart;
  /** Whether two cards share a column, which is what puts one under another. */
  const sharesColumns = (card: DashboardGridCard) =>
    hasGridPlacement(card) &&
    card.colStart < target.colEnd &&
    card.colEnd > target.colStart;
  const others = placed.filter(
    (card): card is PlacedGridCard =>
      card.cardId !== cardId && card.visible && hasGridPlacement(card),
  );

  const next = new Map<string, DashboardGridCard>();

  if (side === "left" || side === "right") {
    const grows = side === "right" ? delta > 0 : delta < 0;
    if (grows) {
      const atEdge =
        side === "right" ? target.colEnd > columns : target.colStart <= 1;
      if (atEdge) return null;
      // The widgets against this side, which give up the column; a side with
      // none is growing into empty space.
      const against = others.filter(
        (card) =>
          sharesRows(card) &&
          (side === "right"
            ? card.colStart === target.colEnd
            : card.colEnd === target.colStart),
      );
      if (
        against.some((card) => card.colEnd - card.colStart <= widthFloor(card))
      ) {
        return null;
      }
      for (const card of against) {
        const shrunk =
          side === "right"
            ? { ...card, colStart: card.colStart + 1 }
            : { ...card, colEnd: card.colEnd - 1 };
        next.set(card.cardId, {
          ...shrunk,
          colSpan: shrunk.colEnd - shrunk.colStart,
        });
      }
    } else if (target.colEnd - target.colStart <= widthFloor(target)) {
      return null;
    }

    const colStart =
      side === "left" ? target.colStart + delta : target.colStart;
    const colEnd = side === "right" ? target.colEnd + delta : target.colEnd;
    next.set(cardId, {
      ...target,
      colStart,
      colEnd,
      colSpan: colEnd - colStart,
    });
  } else {
    const grows = side === "bottom" ? delta > 0 : delta < 0;
    if (!grows) {
      if (target.rowEnd - target.rowStart <= 1) return null;
      next.set(
        cardId,
        side === "bottom"
          ? { ...target, rowEnd: target.rowEnd - 1 }
          : { ...target, rowStart: target.rowStart + 1 },
      );
    } else {
      const against = others.filter(
        (card) =>
          sharesColumns(card) &&
          (side === "bottom"
            ? card.rowStart === target.rowEnd
            : card.rowEnd === target.rowStart),
      );
      const canGive = against.every((card) => card.rowEnd - card.rowStart > 1);

      if (side === "top") {
        if (target.rowStart <= 1 || !canGive) return null;
        for (const card of against) {
          next.set(card.cardId, { ...card, rowEnd: card.rowEnd - 1 });
        }
        next.set(cardId, { ...target, rowStart: target.rowStart - 1 });
      } else if (canGive) {
        for (const card of against) {
          next.set(card.cardId, { ...card, rowStart: card.rowStart + 1 });
        }
        next.set(cardId, { ...target, rowEnd: target.rowEnd + 1 });
      } else {
        // Something underneath has no row to give, so it goes down a row -
        // and so does whatever is under that, and under that: a widget
        // shifted onto the corner of a third one would be an overlap, so the
        // whole column of them moves together and the page below keeps the
        // arrangement it had.
        const below = (card: PlacedGridCard, of: PlacedGridCard) =>
          card.rowStart >= of.rowEnd &&
          card.colStart < of.colEnd &&
          card.colEnd > of.colStart;
        const carried = new Map<string, PlacedGridCard>();
        const frontier: PlacedGridCard[] = [target];
        while (frontier.length) {
          const above = frontier.pop()!;
          for (const card of others) {
            if (carried.has(card.cardId) || !below(card, above)) continue;
            carried.set(card.cardId, card);
            frontier.push(card);
          }
        }
        for (const card of carried.values()) {
          next.set(card.cardId, {
            ...card,
            rowStart: card.rowStart + 1,
            rowEnd: card.rowEnd + 1,
          });
        }
        next.set(cardId, { ...target, rowEnd: target.rowEnd + 1 });
      }
    }
  }

  const resized = placed.map((card) => next.get(card.cardId) ?? card);
  // The last word on it: a drag that would put two widgets on the same square
  // does not happen at all, whatever the arithmetic above made of it.
  return findOverlappingCards(resized) ? null : resized;
}

export function gridColumnCountForMode(mode: ReportsGridMode): number {
  return mode === "grid" ? REPORTS_DESKTOP_COLUMNS : 1;
}

export function fitCardsToGrid(
  cards: DashboardGridCard[],
  widgetsById: Map<string, ReportLayoutWidget>,
  mode: ReportsGridMode,
): DashboardGridCard[] {
  const columns = gridColumnCountForMode(mode);
  const clamped = cards.map((card) => {
    const widget = widgetsById.get(card.cardId);
    const defaultColSpan = widget
      ? getWidgetColSpanConstraints(widget, columns).defaultColSpan
      : 1;

    return {
      ...card,
      colSpan: clampCardColSpan(
        card.colSpan > 0 ? card.colSpan : defaultColSpan,
        widget,
        columns,
      ),
    };
  });

  if (!usesGridPlacementForMode(mode)) {
    return clamped;
  }

  return ensureGridPlacement(clamped, widgetsById, columns);
}

/** Shared read path for private editor and public share pages. */
export function resolveReportsDashboardVisibleCards(
  cards: DashboardGridCard[],
  widgets: ReportLayoutWidget[],
  mode: ReportsGridMode,
): DashboardGridCard[] {
  const widgetsById = new Map(widgets.map((widget) => [widget.id, widget]));
  return fitCardsToGrid(cards, widgetsById, mode).filter(
    (card) => card.visible,
  );
}
